/**
 * Production-safe logging utility
 * In development: logs everything
 * In production: only logs errors and warnings
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

class Logger {
  private shouldLog(level: LogLevel): boolean {
    // In development, log everything
    if (isDevelopment) {
      return true;
    }

    // In production, only log warnings and errors
    if (isProduction) {
      return level === 'warn' || level === 'error';
    }

    // Fallback: log everything
    return true;
  }

  private formatMessage(prefix: string, ...args: any[]): any[] {
    return [`[${prefix}]`, ...args];
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...this.formatMessage('DEBUG', ...args));
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatMessage('INFO', ...args));
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('WARN', ...args));
    }
  }

  error(...args: any[]): void {
    // Always log errors, even in production
    console.error(...this.formatMessage('ERROR', ...args));
  }

  log(...args: any[]): void {
    // Use info level for general logging
    this.info(...args);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const log = logger.log.bind(logger);
export const logDebug = logger.debug.bind(logger);
export const logInfo = logger.info.bind(logger);
export const logWarn = logger.warn.bind(logger);
export const logError = logger.error.bind(logger);
